import React, { useState, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Download, Loader2, FileText, AlertCircle, ChevronLeft, ChevronRight, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

// Point pdf.js worker at a pinned CDN matching the installed pdfjs-dist.
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const extOf = (name = '') => (name.split('.').pop() || '').toLowerCase();
const kindOf = (file) => {
  const e = extOf(file.original_filename || file.name || '');
  const ct = (file.content_type || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif'].includes(e) || ct.startsWith('image/')) return 'image';
  if (e === 'pdf' || ct.includes('pdf')) return 'pdf';
  if (['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(e) || ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('csv')) return 'sheet';
  if (['docx', 'doc'].includes(e) || ct.includes('wordprocessing') || ct.includes('msword')) return 'docx';
  return 'other';
};

/** Fetch the file as a Blob (using auth header) once, reuse across pages. */
const useFileBlob = (fileId, open) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [arrayBuf, setArrayBuf] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open || !fileId) { setBlobUrl(null); setArrayBuf(null); setErr(null); return; }
    let cancelled = false;
    const t = localStorage.getItem('session_token');
    fetch(`${API}/pollen/files/${fileId}/download`, {
      credentials: 'include',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const buf = await r.arrayBuffer();
        if (cancelled) return;
        setArrayBuf(buf);
        const blob = new Blob([buf], { type: r.headers.get('content-type') || 'application/octet-stream' });
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch((e) => { if (!cancelled) setErr(e.message || 'Failed to load'); });
    return () => {
      cancelled = true;
    };
  }, [fileId, open]);
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);
  return { blobUrl, arrayBuf, err };
};

const PdfPreview = ({ blobUrl }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [width, setWidth] = useState(720);
  const containerRef = React.useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width || 720;
      setWidth(Math.min(1100, Math.max(320, Math.floor(w - 16))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const file = useMemo(() => (blobUrl ? { url: blobUrl } : null), [blobUrl]);
  return (
    <div ref={containerRef} className="w-full" data-testid="doc-preview-pdf">
      <Document
        file={file}
        onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPageNum(1); }}
        loading={<div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading PDF…</div>}
        error={<div className="py-10 text-center text-sm text-destructive">Failed to render PDF.</div>}
      >
        <div className="flex flex-col items-center gap-4">
          {Array.from({ length: numPages }, (_, i) => (
            <div key={i} className="bg-white shadow-sm border rounded">
              <Page
                pageNumber={i + 1}
                width={width}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onRenderSuccess={() => {}}
              />
              <div className="text-[10px] text-muted-foreground text-center py-1">Page {i + 1} / {numPages}</div>
            </div>
          ))}
        </div>
      </Document>
      {numPages > 1 && (
        <div className="sticky bottom-0 flex items-center justify-center gap-3 py-2 text-xs bg-background/90 backdrop-blur border-t">
          <Button variant="ghost" size="sm" className="h-7" onClick={() => {
            const next = Math.max(1, pageNum - 1);
            setPageNum(next);
            containerRef.current?.querySelectorAll('[data-page-number]')?.[next - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}><ChevronLeft className="w-3 h-3" /> Prev</Button>
          <span>Page {pageNum} / {numPages}</span>
          <Button variant="ghost" size="sm" className="h-7" onClick={() => {
            const next = Math.min(numPages, pageNum + 1);
            setPageNum(next);
            containerRef.current?.querySelectorAll('[data-page-number]')?.[next - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}>Next <ChevronRight className="w-3 h-3" /></Button>
        </div>
      )}
    </div>
  );
};

const ImagePreview = ({ blobUrl, name }) => (
  <div className="flex items-center justify-center p-2" data-testid="doc-preview-image">
    <img src={blobUrl} alt={name} className="max-w-full max-h-[80vh] object-contain rounded border bg-white" />
  </div>
);

const SheetPreview = ({ arrayBuf }) => {
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!arrayBuf) return;
    try {
      const wb = XLSX.read(arrayBuf, { type: 'array' });
      setWorkbook(wb);
      setActiveSheet(wb.SheetNames[0]);
    } catch (e) {
      setErr(e.message || 'Could not parse sheet');
    }
  }, [arrayBuf]);
  if (err) return <div className="p-8 text-center text-sm text-destructive">{err}</div>;
  if (!workbook) return <div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Parsing sheet…</div>;
  const sheet = activeSheet && workbook.Sheets[activeSheet];
  const rows = sheet ? XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' }) : [];
  return (
    <div className="space-y-3" data-testid="doc-preview-sheet">
      {workbook.SheetNames.length > 1 && (
        <div className="flex flex-wrap gap-1 sticky top-0 bg-background z-10 pb-2 border-b">
          {workbook.SheetNames.map(name => (
            <Button key={name} size="sm" variant={activeSheet === name ? 'default' : 'outline'}
              className="h-7 text-xs" onClick={() => setActiveSheet(name)}
              data-testid={`doc-sheet-${name}`}
            >{name}</Button>
          ))}
        </div>
      )}
      <div className="overflow-auto border rounded">
        <table className="text-xs w-full border-collapse">
          <tbody>
            {rows.map((row, r) => (
              <tr key={r} className={r === 0 ? 'bg-muted/60 font-semibold' : 'odd:bg-white even:bg-muted/20'}>
                {row.map((cell, c) => (
                  <td key={c} className="border px-2 py-1 whitespace-pre-wrap align-top">{String(cell ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DocxPreview = ({ arrayBuf }) => {
  const [html, setHtml] = useState('');
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!arrayBuf) return;
    mammoth.convertToHtml({ arrayBuffer: arrayBuf })
      .then(r => setHtml(r.value || '<p class="text-muted-foreground">No content.</p>'))
      .catch(e => setErr(e.message || 'Could not render DOCX'));
  }, [arrayBuf]);
  if (err) return <div className="p-8 text-center text-sm text-destructive">{err}</div>;
  if (!html) return <div className="flex items-center justify-center py-16 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Rendering document…</div>;
  return (
    <div
      className="prose prose-sm max-w-none px-4 py-4 bg-white rounded border"
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="doc-preview-docx"
    />
  );
};

const UnsupportedPreview = ({ file }) => (
  <div className="p-12 text-center space-y-3" data-testid="doc-preview-unsupported">
    <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/60" />
    <p className="text-sm text-muted-foreground">
      Preview isn&apos;t available for <span className="font-mono">.{extOf(file?.original_filename)}</span> files yet.
    </p>
    <p className="text-xs text-muted-foreground">Download to open in an external app.</p>
  </div>
);

/**
 * Modal viewer. Renders file content inline — no download required.
 * Supports PDF (all pages), images, Excel/CSV (all sheets), DOCX.
 * Header lets the operator rename the file (display_name override).
 */
const DocumentViewer = ({ open, onClose, file, onDownload, onRenamed }) => {
  const { blobUrl, arrayBuf, err } = useFileBlob(file?.id, open);
  const kind = file ? kindOf(file) : 'other';
  const initialLabel = (file?.display_name && file.display_name.trim()) || file?.original_filename || '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialLabel);
  const [saving, setSaving] = useState(false);

  // Reset the draft whenever the viewer opens on a new file.
  useEffect(() => {
    setDraft(initialLabel);
    setEditing(false);
  }, [initialLabel, open]);

  const saveRename = async () => {
    const next = (draft || '').trim();
    if (!file?.id) return;
    if (next === (file.display_name || '').trim() || (!next && !file.display_name)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/files/${file.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ display_name: next }),
      });
      if (!res.ok) throw new Error('rename failed');
      toast.success('File renamed');
      onRenamed?.({ ...file, display_name: next });
      setEditing(false);
    } catch (e) {
      toast.error(e.message || 'Could not rename');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="document-viewer-dialog">
        <DialogDescription className="sr-only">
          Inline preview for {initialLabel || 'document'}. Click the pencil icon in the title to rename.
        </DialogDescription>
        <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between space-y-0 gap-2">
          <DialogTitle className="flex items-center gap-2 min-w-0 text-base flex-1">
            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
            {editing ? (
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <Input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename();
                    if (e.key === 'Escape') { setEditing(false); setDraft(initialLabel); }
                  }}
                  className="h-8 text-sm"
                  data-testid="doc-viewer-rename-input"
                />
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveRename} disabled={saving} data-testid="doc-viewer-rename-save" title="Save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-emerald-600" />}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(false); setDraft(initialLabel); }} data-testid="doc-viewer-rename-cancel" title="Cancel">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <span className="truncate" title={initialLabel} data-testid="doc-viewer-title">{initialLabel || 'Document'}</span>
                {file?.category && <Badge variant="outline" className="text-[10px]">{file.category}</Badge>}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => setEditing(true)} data-testid="doc-viewer-rename-btn" title="Rename">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </>
            )}
          </DialogTitle>
          <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => onDownload?.(file)} data-testid="doc-viewer-download">
            <Download className="w-3.5 h-3.5 mr-1" /> Download
          </Button>
        </DialogHeader>
        <div className="max-h-[85vh] overflow-auto bg-muted/20">
          {err ? (
            <div className="p-8 text-center text-sm text-destructive" data-testid="doc-preview-error">Couldn&apos;t load the file: {err}</div>
          ) : !blobUrl ? (
            <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…
            </div>
          ) : kind === 'pdf' ? (
            <PdfPreview blobUrl={blobUrl} />
          ) : kind === 'image' ? (
            <ImagePreview blobUrl={blobUrl} name={initialLabel} />
          ) : kind === 'sheet' ? (
            <SheetPreview arrayBuf={arrayBuf} />
          ) : kind === 'docx' ? (
            <DocxPreview arrayBuf={arrayBuf} />
          ) : (
            <UnsupportedPreview file={file} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentViewer;
export { kindOf, extOf };
